import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';

import styles from './styles.module.scss';

import { TBuyRovePropertyLanding } from '@utils/types/landings';
import ParallaxImageSection from '@components/_shared/parallaxImageSection';
import {
  SegmentEventNamesEnum,
  SegmentEventPagesEnum,
  useAnalytics,
} from '@utils/hooks/useAnalytics';
import { postBuyPropertyRequest } from '@api/landings/postBuyPropertyRequest';
import clsx from 'clsx';
import TextInput from '@components/_shared/textInput';
import { isEmail } from '@utils/helpers';
import { PhoneNumberInput } from '@components/_shared/phoneNumberInput';
import Button from '@components/_shared/button';

const BuyRovePropertyContact = (props: TBuyRovePropertyLanding['contact']) => {
  const { title, description, cta } = props;
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [reset, setReset] = useState(false);
  const [pristineState, setPristineState] = useState({
    fullName: true,
    email: true,
    phoneNumber: true,
  });

  const { trackEvent } = useAnalytics();

  const resetForm = () => {
    setFullName('');
    setEmail('');
    setPhoneNumber('');
    setPristineState({
      fullName: true,
      email: true,
      phoneNumber: true,
    });
    setReset(true);
  };

  useEffect(() => {
    if (reset) {
      setReset(false);
    }
    return () => {
      resetForm();
    };
  }, [reset]);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (success) {
      timeout = setTimeout(() => {
        setSuccess(false);
        resetForm();
      }, 5000);
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [success]);

  const onSubmit = async () => {
    setSubmitting(true);

    try {
      await postBuyPropertyRequest({
        email,
        phone: phoneNumber,
        full_name: fullName,
      });
      setSuccess(true);
      trackEvent(SegmentEventNamesEnum.BUY_ROVE_PROPERTY_FORM_SUBMITTED, {
        PageName: SegmentEventPagesEnum.BUY_ROVE_PROPERTY_PAGE,
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error(
        'There was an error submitting your information. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.contact} id="contact">
      <div className={styles.contact__wrapper}>
        <div className={styles.contact__content}>
          <h2 className={styles.contact__title}>{title}</h2>
          <p className={styles.contact__description}>{description}</p>
        </div>

        <div className={styles.contactForm}>
          {success ? (
            <div className={styles.contactForm__form}>
              <svg
                width="65"
                height="64"
                viewBox="0 0 65 64"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect x="0.5" width="64" height="64" rx="32" fill="#5CB3B6" />
                <path
                  d="M21 33.25L29 39.75L44 24.25"
                  stroke="white"
                  strokeWidth="3"
                />
              </svg>

              <div className={styles.contactForm__successText}>
                Thank you for submitting your information. <br />
                We will be in touch shortly.
              </div>
            </div>
          ) : (
            <div className={styles.contactForm__form}>
              <TextInput
                label="Full Name"
                placeholder="John Doe"
                containerStyle={clsx([
                  styles.textInput,
                  !pristineState.fullName && !fullName && styles.withError,
                ])}
                onTextChange={setFullName}
                onBlur={() =>
                  setPristineState({ ...pristineState, fullName: false })
                }
                theme="dark"
                withError={false}
                reset={reset}
              />

              <TextInput
                label="Email"
                placeholder="john@doe.com"
                type="email"
                containerStyle={clsx([
                  styles.textInput,
                  !pristineState.email && !isEmail(email) && styles.withError,
                ])}
                onTextChange={setEmail}
                onBlur={() =>
                  setPristineState({ ...pristineState, email: false })
                }
                theme="dark"
                withError={false}
                reset={reset}
              />

              <PhoneNumberInput
                required
                name="phone"
                label="Phone Number"
                placeholder="888-123-4567"
                onTextChange={setPhoneNumber}
                onBlur={() =>
                  setPristineState({ ...pristineState, phoneNumber: false })
                }
                containerStyle={clsx([
                  styles.textInput,
                  !pristineState.phoneNumber &&
                    !phoneNumber &&
                    styles.withError,
                ])}
                noError
              />

              <Button
                onClick={onSubmit}
                loading={submitting}
                containerStyle={styles.submitButton}
                variant="greenContained"
                disabled={!isEmail(email) || !fullName || !phoneNumber}
              >
                {cta}
              </Button>
            </div>
          )}
        </div>
      </div>

      <ParallaxImageSection
        uniqueElementID="buy-rove-property-parallax"
        imageUrl="/images/buy-rove-property-parallax-image.png"
        containerStyle={styles.parallaxImageContainer}
      />
    </div>
  );
};

export default BuyRovePropertyContact;
